import { Alert, Button, Card, CardContent, Divider, Grid, Stack, Typography } from '@mui/material';
import React, { forwardRef } from 'react';
import WholePageSpinner from '../../common/WholePageSpinner';
import { getError } from '../../helpers/checkout';
import { CheckoutStep } from '../../models/CheckoutSession';
import { useAppSelector } from '../../redux/hooks';
import {
  selectActiveStep,
  selectItems,
  selectOrderTotals,
  selectParams,
  selectSettings,
} from '../../redux/selectors/checkoutSelectors';
import ItemList from './ItemList';
import useCurrencyDisplay from './useCurrencyDisplay';
import usePlaceOrder from './usePlaceOrder';

export default forwardRef<HTMLDivElement>(function ReviewCard(_, ref) {
  const params = useAppSelector(selectParams);
  const items = useAppSelector((state) => selectItems(state, params));
  const settings = useAppSelector((state) => selectSettings(state, params?.orderType));
  const activeStep = useAppSelector((state) => selectActiveStep(state, params));
  const { subtotal, shipping, handlingFee, tax, total } = useAppSelector((state) =>
    selectOrderTotals(state, params),
  );
  const { format } = useCurrencyDisplay();

  const { placeOrder, loadingPlaceOrder, submitOrderLoading, submitOrderError } = usePlaceOrder({});

  const handleSubmit = () => {
    placeOrder();
  };

  return (
    <>
      {(loadingPlaceOrder || submitOrderLoading) && (
        <WholePageSpinner text="Submitting Quote" warningText="Please do not refresh the page" />
      )}
      <div ref={ref}>
        <Card variant="outlined">
          <CardContent>
            <Stack direction={'row'} justifyContent={'space-between'}>
              <Typography variant="h5" gutterBottom>
                {'Review'}
              </Typography>
            </Stack>
            {activeStep === CheckoutStep.REVIEW && (
              <>
                <ItemList pageMax={5} items={items ?? []} big />
                <Divider />
                <Grid container spacing={2} mt={2} direction={'row-reverse'}>
                  <Grid size={{ xs: 12, sm: 6 }} px={2}>
                    <Stack spacing={2}>
                      <Stack>
                        {!settings.fulfillmentMethodsDisabled && (
                          <Stack direction={'row'} justifyContent={'space-between'}>
                            <Typography variant="body1">Shipping</Typography>
                            <Typography variant="body1">{format(shipping)}</Typography>
                          </Stack>
                        )}
                        {settings.handlingFeeEnabled && handlingFee > 0 && (
                          <Stack direction={'row'} justifyContent={'space-between'}>
                            <Typography variant="body1">
                              {settings.handlingFeeRenameEnabled
                                ? settings.handlingFeeRenameText
                                : 'Handling Fee'}
                            </Typography>
                            <Typography variant="body1">{format(handlingFee)}</Typography>
                          </Stack>
                        )}
                        <Stack direction={'row'} justifyContent={'space-between'}>
                          <Typography variant="body1">Subtotal</Typography>
                          <Typography variant="body1">{format(subtotal)}</Typography>
                        </Stack>
                        <Stack direction={'row'} justifyContent={'space-between'}>
                          <Typography variant="body1">Tax</Typography>
                          <Typography variant="body1">{format(tax)}</Typography>
                        </Stack>
                        <Stack direction={'row'} justifyContent={'space-between'} mt={1}>
                          <Typography variant="h6">Total</Typography>
                          <Typography variant="h6">{format(total)}</Typography>
                        </Stack>
                      </Stack>
                      {submitOrderError && (
                        <Alert severity="error">
                          Error Submitting Quote: {getError(submitOrderError)}
                        </Alert>
                      )}
                      <Button
                        variant="contained"
                        size="large"
                        loading={loadingPlaceOrder || submitOrderLoading}
                        onClick={handleSubmit}
                        fullWidth
                      >
                        {settings.submitButtonLabel}
                      </Button>
                    </Stack>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    {settings.reviewInstructionsEnabled && (
                      <Alert severity="info">{settings.reviewInstructionsText}</Alert>
                    )}
                  </Grid>
                </Grid>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
});
